/**
 * @package   ScandiPWA\GtmGraphQl
 * @author    Abdelrahman Hady <abdelrahman.hady@scandiweb.com>
 * @copyright Copyright (c) 2023 Scandiweb, Inc (https://scandiweb.com)
 * @license   http://opensource.org/licenses/OSL-3.0 The Open Software License 3.0 (OSL-3.0)
 */

/* eslint-disable quote-props,@scandipwa/scandipwa-guidelines/use-namespace,fp/no-let */
import { BILLING_STEP, DETAILS_STEP, SHIPPING_STEP } from 'Route/Checkout/Checkout.config';
import { isSignedIn } from 'Util/Auth';
import BrowserDatabase from 'Util/BrowserDatabase';

import { EVENT_HANDLE_DELAY } from '../../component/GoogleTagManager/events/BaseEvent.event';
import { CHECKOUT_EVENT_DELAY } from '../../component/GoogleTagManager/events/Checkout.event';
import { FORM_DATA_OBJECT } from '../../component/GoogleTagManager/events/PageDataEvent/Handlers/FormDataEvent.handler';
import {
    CHECKOUT_PRODUCTS_TYPE,
    FORM_TYPE,
    ORDER_TYPE
} from '../../component/GoogleTagManager/events/PageDataEvent/PageData.event';
import Event, {
    EVENT_GTM_BEGIN_CHECKOUT_GA4,
    EVENT_GTM_CHECKOUT,
    EVENT_GTM_PAGE_DATA
} from '../../util/Event';

export const GTM_CART_PAGE_STEP = 1;
export const GTM_SHIPPING_STEP = 2;
export const GTM_BILLING_STEP = 3;
export const GTM_DETAILS_STEP = 4;
export const ADDITIONAL_CART = 'ADDITIONAL_CART';
export const ADDITIONAL_CART_TOTALS = 'ADDITIONAL_CART_TOTALS';

export const checkoutStepMap = {
    SHIPPING_STEP: GTM_SHIPPING_STEP,
    BILLING_STEP: GTM_BILLING_STEP,
    DETAILS_STEP: GTM_DETAILS_STEP
};

export const componentDidMount = (args, callback, instance) => {
    const { totals = {} } = instance.props;
    const { checkoutStep } = instance.state || {};

    if (checkoutStep === SHIPPING_STEP) {
        BrowserDatabase.deleteItem(ADDITIONAL_CART);
        BrowserDatabase.deleteItem(ADDITIONAL_CART_TOTALS);

        setTimeout(
            () => Event.dispatch(EVENT_GTM_BEGIN_CHECKOUT_GA4),
            CHECKOUT_EVENT_DELAY
        );
    }

    setTimeout(
        () => Event.dispatch(EVENT_GTM_CHECKOUT, {
            totals,
            step: checkoutStepMap[checkoutStep]
        }),
        CHECKOUT_EVENT_DELAY
    );

    setTimeout(() => _updatePageDataForm(instance), EVENT_HANDLE_DELAY);

    return callback(...args);
};

export const componentDidUpdate = (args, callback, instance) => {
    const [, prevState] = args;

    const { checkoutStep, isLoading, isDeliveryOptionsLoading } = instance.state;
    const {
        checkoutStep: prevCheckoutStep,
        isDeliveryOptionsLoading: prevIsDeliveryOptionsLoading
    } = prevState;

    if (!isLoading && checkoutStep !== prevCheckoutStep) {
        _updatePageDataForm(instance);
    }

    // Dispatching checkout products event only when the whole shipping page has been loaded
    if (isDeliveryOptionsLoading !== prevIsDeliveryOptionsLoading || checkoutStep === DETAILS_STEP) {
        Event.dispatch(
            EVENT_GTM_PAGE_DATA,
            { type: CHECKOUT_PRODUCTS_TYPE }
        );
    }

    return callback(...args);
};

export const componentWillUnmount = (args, callback) => {
    if (window.pageData) {
        // eslint-disable-next-line fp/no-delete
        delete window.pageData[FORM_DATA_OBJECT];
    }

    return callback(...args);
};

export const _getCheckoutStep = (instance) => {
    const { checkoutStep } = instance.state;

    switch (checkoutStep) {
    case SHIPPING_STEP:
        return GTM_SHIPPING_STEP;
    case BILLING_STEP:
        return GTM_BILLING_STEP;
    case DETAILS_STEP:
        return GTM_DETAILS_STEP;
    default:
        return GTM_CART_PAGE_STEP;
    }
};

export const _updatePageDataForm = (instance) => {
    const checkoutGTMStep = _getCheckoutStep(instance);
    Event.dispatch(EVENT_GTM_PAGE_DATA, {
        type: FORM_TYPE,
        name: 'order',
        formType: 'checkout',
        step: checkoutGTMStep,
        lastStep: GTM_DETAILS_STEP
    });
};

export const aroundSaveAddressInformation = (args, callback) => {
    const { shipping_address: { country_id }, shipping_address } = args[0];
    window.util = { shipping_address };

    if (isSignedIn()) {
        Event.dispatch(
            EVENT_GTM_PAGE_DATA,
            { type: ORDER_TYPE, addressInfo: args }
        );
    } else {
        const countryIdLowerCase = country_id.toLowerCase();

        Event.dispatch(
            EVENT_GTM_PAGE_DATA,
            { type: ORDER_TYPE, shipToCountry: countryIdLowerCase }
        );
    }

    return callback(...args);
};

export const aroundSetDetailsStep = (args, callback) => {
    const [orderID, status_label] = args;

    Event.dispatch(
        EVENT_GTM_PAGE_DATA,
        {
            type: ORDER_TYPE, id: orderID, status: status_label, mods: { step: 'details' }
        }
    );

    return callback(...args);
};

export const aroundSavePaymentInformation = (args, callback) => {
    const { billing_address } = args[0];
    let shipBillSameAddress;

    const areObjectEquals = (object1, object2) => {
        const keys1 = Object.keys(object1);
        const keys2 = Object.keys(object2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        // eslint-disable-next-line fp/no-loops,no-restricted-syntax,no-unused-vars
        for (const key of keys1) {
            if (object1[key] !== object2[key]) {
                return false;
            }
        }

        return true;
    };

    const { util: { shipping_address } } = window;

    if (isSignedIn() && !shipping_address) {
        const { signedInAddress } = window.util;
        const { shipping_address: signed_in_shipping_address } = signedInAddress[0];

        shipBillSameAddress = areObjectEquals(billing_address, signed_in_shipping_address);
    } else {
        shipBillSameAddress = areObjectEquals(billing_address, shipping_address);
    }

    Event.dispatch(
        EVENT_GTM_PAGE_DATA,
        { type: ORDER_TYPE, shipBillSameAddress }
    );

    return callback(...args);
};

export default {
    'Route/Checkout/Container': {
        'member-function': {
            'componentDidMount': componentDidMount,
            'componentDidUpdate': componentDidUpdate,
            'saveAddressInformation': aroundSaveAddressInformation,
            'savePaymentInformation': aroundSavePaymentInformation,
            'setDetailsStep': aroundSetDetailsStep,
            'componentWillUnmount': componentWillUnmount
        }
    }
};
